<template>

    <LoadingPage v-if="disableSendingButton"></LoadingPage>

    <div class="row text-center justify-content-center">
        <h2 class="modal-title"><strong>Send Contract</strong></h2>
    </div>

    <div class="row mt-2 mb-2 text-center">
        <h6 class="card-subtitle text-muted">Date: <strong>{{ this.currentDate }}</strong></h6>
    </div>

    <form ref="contractFormRef" class="row g-0 mx-4" style="gap: 0.5rem">
        <div class="mb-3">
            <label for="sentTo" class="form-label">To<span class="text-danger">*</span> :</label>
            <div class="email-input-container">
                <div v-for="(recipient, index) in sentToRecipients" :key="index" class="email-pill" :class="{ 'email-pill-mandatory': recipient.mandatory }">
                    <span>{{ recipient.displayName || recipient.email }}</span>
                    <button v-if="!recipient.mandatory" type="button" class="email-pill-remove" @click="removeRecipient(index, 'sentTo')">×</button>
                </div>
            </div>
        </div>
        <div class="pt-3 pb-3 justify-content-center d-flex">
            <button type="button" class="btn btn-info text-light" @click="sendContract()"
                :disabled="disableFormButton() || disableSendingButton">
                <i class="fas fa-paper-plane me-2"></i> Send
            </button>
        </div>
    </form>
</template>

<script>
import { log, isValidEmail, cleanEmailString, extractEmails, parseEmailRecipient } from '@/plugins/helper.js'
import LoadingPage from '@/components/Utilities/vertex-loading.vue'
export default {

    components: {
        LoadingPage
    },

    props: {
        initialSendTo: { // Prop for initial 'To' recipients (signer emails)
            type: Array,
            default: () => []
        },
        contractID: { // Prop for Contract ID
            type: String,
            default: ''
        },
        hasEditor: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            currentDate: new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }),
            sentToRecipients: [],
            currentSentToInput: '',
            disableSendingButton: false,
        };
    },

    emits: ["contractSentSuccess"],

    methods: {
        isEmailValid(email) {
            return isValidEmail(email);
        },

        parseEmailInput(input) {
            const parsed = parseEmailRecipient(input);
            if (parsed && this.isEmailValid(parsed.email)) {
                return parsed;
            }
            return null;
        },

        addRecipient() {
            let inputField = this.currentSentToInput;
            let recipientArray = this.sentToRecipients;

            if (!inputField || !inputField.trim()) return;

            const extracted = extractEmails(inputField);
            if (extracted.length > 0) {
                extracted.forEach(email => {
                    const parsed = this.parseEmailInput(email) || { displayName: email, email };
                    if (parsed && this.isEmailValid(parsed.email)) {
                        const exists = recipientArray.some(r => r.email === parsed.email);
                        if (!exists) {
                            recipientArray.push(parsed);
                        }
                    }
                });
            } else {
                const rawInputs = inputField.split(/[,;]/).map(s => s.trim()).filter(Boolean);
                rawInputs.forEach(rawInput => {
                    const parsed = this.parseEmailInput(rawInput);
                    if (parsed && this.isEmailValid(parsed.email)) {
                        const exists = recipientArray.some(r => r.email === parsed.email);
                        if (!exists) {
                            recipientArray.push(parsed);
                        }
                    }
                });
            }

            this.currentSentToInput = '';
        },

        removeRecipient(index, field) {
            if (field === 'sentTo') {
                this.sentToRecipients.splice(index, 1);
            }
        },

        handlePaste(field, event) {
            event.preventDefault();
            const pastedText = (event.clipboardData || window.clipboardData)?.getData('text') || '';
            const emails = extractEmails(pastedText);

            let targetRecipients = this.sentToRecipients;

            if (emails.length > 0) {
                emails.forEach(email => {
                    const parsed = this.parseEmailInput(email) || { displayName: email, email };
                    if (parsed && this.isEmailValid(parsed.email)) {
                        const exists = targetRecipients.some(r => r.email === parsed.email);
                        if (!exists) {
                            targetRecipients.push(parsed);
                        }
                    }
                });
                this.currentSentToInput = '';
            } else {
                const cleaned = cleanEmailString(pastedText);
                this.currentSentToInput = cleaned || pastedText;
            }
        },

        async sendContract() {

            if (this.hasEditor) {
                const editorDirty = this.$parent.$refs.editorRef && this.$parent.$refs.editorRef.isEditorContentDirty();
                const discountOk = this.$parent.$refs.contractDetailsFormRef ? this.$parent.$refs.contractDetailsFormRef.isShowDiscountChanged() : true;
                if (editorDirty || discountOk === false) {
                    this.$toast.error("There are unsaved changes.");
                    return
                }
            }
            this.disableSendingButton = true

            let sentToEmails = this.sentToRecipients.map(recipient => recipient.email);

            let forSending = {
                sent_to: sentToEmails,
                cc: [],
                bcc: [],
                custom_message: ''
            }

            log("Contract ID:", this.contractID)
            log("For Sending:", forSending)

            await this.$axios.post("/contracts/" + this.contractID + "/send", forSending)
                .then((response) => {
                    if (response.data.success) {
                        this.$toast.success(response.data.message)
                        this.$parent.proposal_client_email = ''
                        this.$emit("contractSentSuccess")
                        this.resetForm();
                        this.disableSendingButton = false
                    } else {
                        this.$toast.error(response.data.message)
                        this.disableSendingButton = false
                    }
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                    this.disableSendingButton = false
                })
        },

        resetForm() {
            this.sentToRecipients = [];
            this.currentSentToInput = '';
        },

        disableFormButton() {
            return this.sentToRecipients.length === 0;
        },

        updateInitialSendTo(propValue) {
            this.sentToRecipients = [];

            if (propValue && Array.isArray(propValue)) {
                propValue.forEach(email => {
                    const parsedRecipient = this.parseEmailInput(email);
                    if (parsedRecipient && this.isEmailValid(parsedRecipient.email)) {
                        parsedRecipient.mandatory = true;
                        this.sentToRecipients.push(parsedRecipient);
                    } else {
                        console.warn('Signer email is not a valid email format:', email);
                    }
                });
                console.log('Initial signer recipients updated:', this.sentToRecipients);
            }
        },
    },

    watch: {
        initialSendTo: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.updateInitialSendTo(newVal);
                }
            },
            immediate: true
        }
    },

    mounted() {
        if (this.initialSendTo && Array.isArray(this.initialSendTo) && this.initialSendTo.length > 0) {
            this.updateInitialSendTo(this.initialSendTo);
        }
    },

    created() {
        this.currentDate = new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    }
};
</script>

<style>
/* Container for the pills and input field */
.email-input-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    padding: 0.375rem 0.75rem;
    min-height: 38px;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.075);
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.email-input-container:focus-within {
    border-color: #86b7fe;
    outline: 0;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Styling for each individual email pill */
.email-pill {
    display: inline-flex;
    align-items: center;
    background-color: #00a0c1;
    border: 1px solid #0087a3;
    border-radius: 16px;
    padding: 0.2rem 0.5rem;
    margin-right: 0.5rem;
    margin-bottom: 0.2rem;
    font-size: 0.9em;
    color: #ffffff;
    white-space: nowrap;
}

.email-pill-remove {
    background: none;
    border: none;
    color: #e0e0e0;
    font-size: 1.1em;
    line-height: 1;
    margin-left: 0.4rem;
    cursor: pointer;
    padding: 0 0.2rem;
    border-radius: 50%;
    transition: background-color 0.2s, color 0.2s;
}

.email-pill-remove:hover {
    background-color: #007c97;
    color: #ffffff;
}

/* Mandatory signer pills - slightly different styling to indicate they cannot be removed */
.email-pill-mandatory {
    background-color: #007c97;
    border-color: #006780;
    padding-right: 0.75rem;
}

/* Styling for the actual input field within the container */
.email-input-field {
    flex-grow: 1;
    border: none;
    outline: none;
    padding: 0;
    min-width: 80px;
    margin: 0;
    font-size: 1em;
    background: transparent;
}

.email-input-field:focus {
    outline: none;
    box-shadow: none;
}
</style>
